import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';

interface PaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

const ArrowButton = ({
  children,
  onClick,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-6 h-6 flex items-center justify-center ${
      disabled
        ? 'text-neutral-300 cursor-not-allowed'
        : 'text-neutral-700 hover:text-neutral-900'
      }`}
  >
    {children}
  </button>
);

const PaginationButton = ({
  children,
  onClick,
  isActive = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  isActive?: boolean;
}) => (
  <button
    onClick={onClick}
    className={`w-8 h-8 rounded-lg text-sm flex items-center justify-center ${
      isActive
        ? 'bg-neutral-700 text-neutral-100'
        : `bg-neutral-100 border border-neutral-300 text-neutral-500
          hover:border-neutral-400 border-solid`
      }`}
  >
    {children}
  </button>
);

export function Pagination({
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
}: PaginationProps) {
  if (totalCount <= pageSize) {
    return null;
  }

  const totalPages = Math.ceil(totalCount / pageSize);
  const currentPageDisplay = currentPage + 1;
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === totalPages - 1;

  return (
    <div className="flex justify-end items-center gap-4 mt-4">
      <ArrowButton
        onClick={() => onPageChange(Math.max(0, currentPage - 1))}
        disabled={isFirstPage}
      >
        <MdKeyboardArrowLeft />
      </ArrowButton>

      {totalPages > 3 && currentPageDisplay > 2 && (
        <>
          <PaginationButton onClick={() => onPageChange(0)}>1</PaginationButton>
          <span className="text-neutral-500">...</span>
        </>
      )}

      {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
        let pageNum;
        if (totalPages <= 3) {
          pageNum = i + 1;
        } else if (currentPageDisplay === 1) {
          pageNum = i + 1;
        } else if (currentPageDisplay === totalPages) {
          pageNum = totalPages - 2 + i;
        } else {
          pageNum = currentPageDisplay - 1 + i;
        }

        return (
          <PaginationButton
            key={pageNum}
            onClick={() => onPageChange(pageNum - 1)}
            isActive={pageNum === currentPageDisplay}
          >
            {pageNum}
          </PaginationButton>
        );
      })}

      {totalPages > 3 && currentPageDisplay < totalPages - 1 && (
        <>
          <span className="text-neutral-500">...</span>
          <PaginationButton onClick={() => onPageChange(totalPages - 1)}>
            {totalPages}
          </PaginationButton>
        </>
      )}

      <ArrowButton
        onClick={() => onPageChange(Math.min(totalPages - 1, currentPage + 1))}
        disabled={isLastPage}
      >
        <MdKeyboardArrowRight />
      </ArrowButton>
    </div>
  );
}
